import { findPreferredStatus } from '../FlowchartView/utils/statusUtils';
import { DEFAULT_STATUSES } from '../FlowchartView/TaskNode/constants';
import {
  isTickActive,
  isTickChecked,
  normalizeTick,
  readMasterTick,
} from '../../DocEditor/utils/tickOverrides';
import { countMissionDocFiles } from '../../DocEditor/utils/fileOverrides';

/**
 * All sprint IDs in a tree rooted at rootId (including root), using parentSprintId links.
 */
export function collectDescendantSprintIds(rootId, allSprints = []) {
  if (!rootId) return new Set();
  const ids = new Set([rootId]);
  let frontier = [rootId];
  while (frontier.length) {
    const next = [];
    for (const pid of frontier) {
      for (const s of allSprints) {
        if (s.parentSprintId === pid && !ids.has(s.id)) {
          ids.add(s.id);
          next.push(s.id);
        }
      }
    }
    frontier = next;
  }
  return ids;
}

/** Non-archived missions on a sprint. */
export function getActiveTasks(sprint) {
  return (sprint?.tasks || []).filter((t) => t && !t.archived);
}

/**
 * Split active missions for a campaign tree into root vs nested.
 * `rootSprint` should be the live local sprint (may be fresher than allSprints).
 */
export function collectCampaignTreeTasks(rootId, rootSprint, allSprints = []) {
  const descendantIds = collectDescendantSprintIds(rootId, allSprints);
  const rootTasks = getActiveTasks(rootSprint);
  const nestedTasks = [];
  for (const id of descendantIds) {
    if (id === rootId) continue;
    const sp = allSprints.find((s) => s.id === id);
    nestedTasks.push(...getActiveTasks(sp));
  }
  return { rootTasks, nestedTasks, allTasks: [...rootTasks, ...nestedTasks], descendantIds };
}

/** Unique attached doc IDs across missions. */
export function collectAttachedDocIds(tasks = []) {
  const ids = new Set();
  for (const task of tasks) {
    if (!Array.isArray(task?.attachedDocs)) continue;
    for (const entry of task.attachedDocs) {
      if (entry?.docId) ids.add(entry.docId);
    }
  }
  return [...ids];
}

/** Walk a doc body tree; Map of checkbox blockId → master tick default. */
export function collectCheckboxTicks(tree, into = new Map()) {
  if (!Array.isArray(tree)) return into;
  for (const block of tree) {
    if (block?.type === 'checkbox') {
      into.set(block.id, readMasterTick(block));
    }
    if (block?.children?.length) collectCheckboxTicks(block.children, into);
    if (block?.type === 'table' && block.props?.tableData) {
      const cells = [
        ...(block.props.tableData.headers || []),
        ...(block.props.tableData.rows || []).flat(),
      ];
      for (const cell of cells) {
        if (Array.isArray(cell)) collectCheckboxTicks(cell, into);
      }
    }
  }
  return into;
}

/**
 * Same semantics as LibraryDocPane "Uncheck All":
 * explicit false for every master-checked checkbox; omit master-false ids.
 */
export function buildUncheckOverrides(masterTicks) {
  const next = {};
  if (!masterTicks) return next;
  masterTicks.forEach((checked, id) => {
    if (isTickActive(checked)) next[id] = false;
  });
  return next;
}

/**
 * Build per-doc uncheck override maps from loaded docs (id → full doc with body).
 */
export function buildDocUncheckMaps(docsById) {
  const maps = new Map();
  if (!docsById) return maps;
  const entries = docsById instanceof Map ? docsById.entries() : Object.entries(docsById);
  for (const [docId, doc] of entries) {
    const master = collectCheckboxTicks(doc?.body);
    maps.set(docId, buildUncheckOverrides(master));
  }
  return maps;
}

/**
 * Apply Uncheck-All style docTicks for a task's attached docs.
 * Falls back to clearing docTicks when docs aren't available.
 */
export function resetTaskDocTicks(task, docsById = null) {
  if (!Array.isArray(task?.attachedDocs) || task.attachedDocs.length === 0) {
    return {};
  }
  if (!docsById) return {};

  const next = {};
  for (const entry of task.attachedDocs) {
    const docId = entry?.docId;
    if (!docId) continue;
    const doc = docsById instanceof Map ? docsById.get(docId) : docsById[docId];
    if (!doc?.body) continue;
    const overrides = buildUncheckOverrides(collectCheckboxTicks(doc.body));
    if (Object.keys(overrides).length > 0) {
      next[docId] = overrides;
    }
  }
  return next;
}

/**
 * Count currently-checked checkboxes (effective = override ?? master) across
 * mission-attached docs, plus how many unique docs have at least one checked box.
 */
export function countCheckedDocProgress(tasks = [], docsById) {
  let checkboxCount = 0;
  const docsWithChecked = new Set();
  if (!docsById) return { checkboxCount: 0, documentCount: 0 };

  for (const task of tasks) {
    if (!Array.isArray(task?.attachedDocs)) continue;
    const tickBag = task.docTicks && typeof task.docTicks === 'object' ? task.docTicks : {};
    for (const entry of task.attachedDocs) {
      const docId = entry?.docId;
      if (!docId) continue;
      const doc = docsById instanceof Map ? docsById.get(docId) : docsById[docId];
      if (!doc?.body) continue;
      const master = collectCheckboxTicks(doc.body);
      const overrides = tickBag[docId] && typeof tickBag[docId] === 'object' ? tickBag[docId] : {};
      let anyChecked = false;
      master.forEach((masterChecked, blockId) => {
        const effective = Object.prototype.hasOwnProperty.call(overrides, blockId)
          ? normalizeTick(overrides[blockId])
          : masterChecked;
        if (isTickChecked(effective)) {
          checkboxCount += 1;
          anyChecked = true;
        }
      });
      if (anyChecked) docsWithChecked.add(docId);
    }
  }

  return { checkboxCount, documentCount: docsWithChecked.size };
}

/**
 * Clear decision answers, set status to preferred "not started" / To Do,
 * and uncheck attached-doc checkboxes (mission progress).
 */
export function resetTaskProgress(task, statuses, { skipArchived = true, docsById = null } = {}) {
  if (skipArchived && task.archived) return task;
  const list = Array.isArray(statuses) && statuses.length ? statuses : DEFAULT_STATUSES;
  const preferred = findPreferredStatus(list, 'not_started');
  const todoId = preferred?.id || 'pending';
  const base = {
    ...task,
    status: todoId,
    completedAt: null,
    startedAt: null,
    blockedReason: null,
    blockedAt: null,
    docTicks: resetTaskDocTicks(task, docsById),
    docFiles: {},
    variableValues: [],
  };
  if (task.nodeType === 'decision') {
    return {
      ...base,
      answer: null,
      selectedOption: undefined,
    };
  }
  return base;
}

export function resetTasksProgressForStatuses(tasks, statuses, options) {
  return (tasks || []).map((t) => resetTaskProgress(t, statuses, options));
}

/**
 * Summarize reset impact for the confirmation modal.
 */
export function summarizeResetImpact({ rootTasks = [], nestedTasks = [], docsById = null } = {}) {
  const allTasks = [...rootTasks, ...nestedTasks];
  const { checkboxCount, documentCount } = countCheckedDocProgress(allTasks, docsById);
  const { fileCount } = countMissionDocFiles(allTasks);
  return {
    rootMissionCount: rootTasks.length,
    nestedMissionCount: nestedTasks.length,
    checkboxCount,
    documentCount,
    fileCount,
  };
}
